import React, { useState, useEffect } from "react";
import { Modal } from "../../components/common/Modal";
import { useApp } from "../../app/context/AppContext";
import { useNotification } from "../../app/context/NotificationContext";

export function ProductModal({ isOpen, onClose, productToEdit }) {
  const { addProduct, updateProduct } = useApp();
  const { addToast } = useNotification();

  const isEdit = Boolean(productToEdit);

  const [formData, setFormData] = useState({
    name: "",
    category: "Grains & Cereals",
    brand: "",
    unit: "Bag",
    barcode: "",
    MRP: 250,
    purchasePrice: 180,
    retailPrice: 230,
    wholesalePrice: 205,
    reorderLevel: 20,
    description: ""
  });

  useEffect(() => {
    if (productToEdit) {
      setFormData({
        name: productToEdit.name || "",
        category: productToEdit.category || "Grains & Cereals",
        brand: productToEdit.brand || "",
        unit: productToEdit.unit || "Bag",
        barcode: productToEdit.barcode || "",
        MRP: productToEdit.MRP || 250,
        purchasePrice: productToEdit.purchasePrice || 180,
        retailPrice: productToEdit.retailPrice || 230,
        wholesalePrice: productToEdit.wholesalePrice || 205,
        reorderLevel: productToEdit.reorderLevel || 20,
        description: productToEdit.description || ""
      });
    } else {
      setFormData({
        name: "",
        category: "Grains & Cereals",
        brand: "",
        unit: "Bag",
        barcode: `890${Math.floor(1000000000 + Math.random() * 9000000000)}`,
        MRP: 250,
        purchasePrice: 180,
        retailPrice: 230,
        wholesalePrice: 205,
        reorderLevel: 20,
        description: ""
      });
    }
  }, [productToEdit, isOpen]);

  const categories = [
    "Grains & Cereals",
    "Flours & Grains",
    "Edible Oils & Ghee",
    "Pulses & Dals",
    "Spices & Condiments",
    "Sugar & Sweeteners",
    "Snacks & Biscuits",
    "Packaged Foods",
    "Beverages & Tea",
    "Sauces & Condiments",
    "Personal & Home Care"
  ];

  const units = ["Bag", "Pouch", "Pkt", "Tin", "Jar", "Bottle", "Box", "Pack", "Kg", "Liter"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await updateProduct(productToEdit.id, formData);
        addToast(`Product "${formData.name}" updated successfully!`, "success");
      } else {
        await addProduct(formData);
        addToast(`New product "${formData.name}" added to catalog!`, "success");
      }
      onClose();
    } catch (err) {
      addToast("Failed to save product details.", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? `Edit Product Master: ${productToEdit.name}` : "Create Master Product SKU"}
      subtitle="Register master grocery item with retail, wholesale & purchase price tiers"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Product Full Name & Pack Size *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Daawat Rozana Basmati Rice (5kg)"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              FMCG Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Brand / Manufacturer
            </label>
            <input
              type="text"
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              placeholder="e.g. Tata, Fortune, HUL, MDH"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Unit of Measurement (UOM) *
            </label>
            <select
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              {units.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              EAN Barcode *
            </label>
            <input
              type="text"
              required
              value={formData.barcode}
              onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono"
            />
          </div>
        </div>

        {/* 4 Price Tiers Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 dark:bg-slate-950 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800">
          <div>
            <label className="block text-slate-500 font-semibold mb-1">Purchase Cost (₹) *</label>
            <input
              type="number"
              required
              value={formData.purchasePrice}
              onChange={(e) => setFormData({ ...formData, purchasePrice: Number(e.target.value) })}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg font-mono font-bold"
            />
          </div>
          <div>
            <label className="block text-slate-500 font-semibold mb-1">Wholesale (₹) *</label>
            <input
              type="number"
              required
              value={formData.wholesalePrice}
              onChange={(e) => setFormData({ ...formData, wholesalePrice: Number(e.target.value) })}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg font-mono font-bold text-sky-600 dark:text-sky-400"
            />
          </div>
          <div>
            <label className="block text-slate-500 font-semibold mb-1">Retail Price (₹) *</label>
            <input
              type="number"
              required
              value={formData.retailPrice}
              onChange={(e) => setFormData({ ...formData, retailPrice: Number(e.target.value) })}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg font-mono font-bold text-emerald-600 dark:text-emerald-400"
            />
          </div>
          <div>
            <label className="block text-slate-500 font-semibold mb-1">Max MRP (₹) *</label>
            <input
              type="number"
              required
              value={formData.MRP}
              onChange={(e) => setFormData({ ...formData, MRP: Number(e.target.value) })}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg font-mono text-slate-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Minimum Reorder Level (Low Stock Threshold) *
            </label>
            <input
              type="number"
              min="1"
              value={formData.reorderLevel}
              onChange={(e) => setFormData({ ...formData, reorderLevel: Number(e.target.value) })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Product Description / Specs
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g. Moisture tested, pure Sharbati grain"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/30"
          >
            {isEdit ? "Update Master Product" : "Save Product SKU"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function StockAdjustmentModal({ isOpen, onClose }) {
  const { products, warehouses, batches, adjustStock } = useApp();
  const { addToast } = useNotification();

  const [productId, setProductId] = useState(products[0]?.id || "");
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id || "");
  const [binCode, setBinCode] = useState("A1-S1-B1");
  const [physicalCount, setPhysicalCount] = useState(140);
  const [reason, setReason] = useState("Monthly Physical Cycle Count / Audit Variance");
  const [approver, setApprover] = useState("Narayan Murthy Hegde (Owner)");

  const selectedBatch = batches.find((b) => b.productId === productId && b.warehouseId === warehouseId) || batches[0];
  const systemCount = selectedBatch?.qtyRemaining || 0;
  const variance = Number(physicalCount) - Number(systemCount);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await adjustStock(productId, warehouseId, binCode, Number(physicalCount), Number(systemCount), reason, approver);
      addToast(`Stock adjustment committed! Variance: ${variance > 0 ? "+" : ""}${variance} units.`, "success");
      onClose();
    } catch (err) {
      addToast("Failed to commit stock adjustment.", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Physical Stock Reconciliation & Adjustment"
      subtitle="Reconcile physical inventory count against system ledger with mandatory audit approval"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Select Product SKU *
          </label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          >
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Warehouse *
            </label>
            <select
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Shelf / Bin Code *
            </label>
            <input
              type="text"
              value={binCode}
              onChange={(e) => setBinCode(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono uppercase"
            />
          </div>
        </div>

        {/* Side-by-Side Comparison */}
        <div className="grid grid-cols-3 gap-3 p-3.5 bg-slate-100 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 text-center">
          <div>
            <p className="text-[10px] text-slate-500 font-semibold">System Stock</p>
            <p className="text-xl font-bold font-mono text-slate-800 dark:text-slate-200 mt-1">
              {systemCount}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">
              Physical Count *
            </p>
            <input
              type="number"
              min="0"
              value={physicalCount}
              onChange={(e) => setPhysicalCount(e.target.value)}
              className="w-20 mx-auto text-center font-bold text-lg font-mono bg-white dark:bg-slate-900 border border-emerald-500 rounded-lg mt-0.5"
            />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 font-semibold">Variance</p>
            <p
              className={`text-xl font-bold font-mono mt-1 ${
                variance === 0 ? "text-slate-500" : variance > 0 ? "text-emerald-600" : "text-rose-500"
              }`}
            >
              {variance > 0 ? `+${variance}` : variance}
            </p>
          </div>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Reason for Adjustment *
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          >
            <option value="Monthly Physical Cycle Count / Audit Variance">Monthly Physical Cycle Count / Audit Variance</option>
            <option value="Packaging Leakage / Broken Pouch Write-off">Packaging Leakage / Broken Pouch Write-off</option>
            <option value="Moisture Shrinkage / Grain Handling Loss">Moisture Shrinkage / Grain Handling Loss</option>
            <option value="Found Unrecorded Supplier Excess Stock">Found Unrecorded Supplier Excess Stock</option>
          </select>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Authorizing Approver *
          </label>
          <input
            type="text"
            value={approver}
            onChange={(e) => setApprover(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          />
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white font-semibold rounded-xl shadow-lg shadow-rose-600/30"
          >
            Authorize & Commit Reconciliation
          </button>
        </div>
      </form>
    </Modal>
  );
}
